import {Subscription, combineLatest as observableCombineLatest} from 'rxjs';
import {distinctUntilChanged, filter, switchMap, map, take, pairwise} from 'rxjs/operators';
import {Component, HostBinding, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Route, Router, RouterEvent, Routes, RoutesRecognized, Scroll} from '@angular/router';
import {Store, select} from '@ngrx/store';


import {ngcAnimations} from './comp/animations';
import {
    getAuthenticatedUser,
    getAuthenticatedUserPreferences,
    getPendingRequests,
    getProviderApplications,
    getVendorInstitutions,
    State
} from '../app.reducers';
import {getIsDeletingApplication} from '@app/views/admin/application-settings/store/application-settings.reducers';
import * as integrationActions from './reducers/integration/integration.actions';
import {User} from './model/user';
import {AuthenticatedUserService} from './service/authenticated-user.service';
import {ApplicationProviderListModel} from '@app/views/admin/application-settings/models/application-provider-list.model';
import {InstitutionsVendorListModel} from '@app/core/model/institutional-model';
import {DataAccessRequestModel} from '@app/core/model/integration-models';
import {isLoginRoute, routes as mainModuleRoutes} from '@app/core/main.module.routes';

@Component({
    selector: 'ngc-content',
    templateUrl: './content.component.html',
    styleUrls: ['./content.component.scss'],
    animations: ngcAnimations
})
export class ContentComponent implements OnInit, OnDestroy {
    @HostBinding('@routerTransitionUp')
        routeAnimationUp = false;

    @HostBinding('@routerTransitionDown')
        routeAnimationDown = false;

    @HostBinding('@routerTransitionRight')
        routeAnimationRight = false;

    @HostBinding('@routerTransitionLeft')
        routeAnimationLeft = false;

    @HostBinding('@routerTransitionFade')
        routeAnimationFade = false;

    subscriptions: Subscription[] = [];

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private store: Store<State>,
        private authenticatedUserService: AuthenticatedUserService
    ) {
        this.subscriptions.push(
            this.router.events.pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute)
            ).subscribe((event) => {
                this.routeAnimationRight = !this.routeAnimationRight;
                /*
                this.routeAnimationFade = !this.routeAnimationFade;
                this.routeAnimationUp = !this.routeAnimationUp;
                this.routeAnimationDown = !this.routeAnimationDown;
                this.routeAnimationRight = !this.routeAnimationRight;
                this.routeAnimationLeft = !this.routeAnimationLeft;
                */
            })
        );

        this.subscriptions.push(
            this.router.events.pipe(
                filter(event => event instanceof RoutesRecognized),
                pairwise()
            ).subscribe(([prevEvent, newEvent]: [RoutesRecognized, RoutesRecognized]) => {
                if (!isLoginRoute(prevEvent.urlAfterRedirects)) {
                    sessionStorage.setItem('previousPage', encodeURIComponent(prevEvent.urlAfterRedirects));
                }
            })
        );

        this.subscriptions.push(
            observableCombineLatest(
                this.store.pipe(
                    select(getAuthenticatedUser),
                    filter(user => !!user),
                    distinctUntilChanged((prev: User, curr: User) => !!(prev && curr && prev['username'] === curr['username'] && prev['orgGuid'] === curr['orgGuid'])),
                ),
                this.router.events.pipe(
                    filter((event) => event instanceof NavigationEnd),
                    map((event: RouterEvent) => (event instanceof Scroll) ? event.routerEvent : event),
                    distinctUntilChanged((a: NavigationEnd, b: NavigationEnd) =>
                        a.url && a.url.indexOf('/databrowsing') > -1 && b.url && b.url.indexOf('/databrowsing') > -1
                    )
                )
            ).subscribe(([user, event]: [User, NavigationEnd]) => {

                // disable events on login-like pages
                if (event.url && this.isMainModuleRoute(event.url)) {
                    return;
                }

                const isProvider = this.authenticatedUserService.isProvider(user);
                const isVendor = this.authenticatedUserService.isVendor(user);

                // disable events for databrowsing
                if (event.url && event.url.indexOf('/databrowsing') > -1 && event.urlAfterRedirects.indexOf('/databrowsing') > -1) {
                    if (isProvider || isVendor) {
                        this.subscriptions.push(
                            this.store.pipe(select(getPendingRequests))
                                .pipe(take(1))
                                .subscribe((pendingRequests: DataAccessRequestModel[]) => {
                                    if (pendingRequests === undefined) {
                                        this.store.dispatch(new integrationActions.GetPendingRequestsAction());
                                    }
                                })
                        );
                    }

                    if (isProvider) {
                        this.store.dispatch(new integrationActions.GetProviderApplicationsAction());
                    } else if (isVendor) {
                        this.subscriptions.push(
                            this.store.pipe(select(getVendorInstitutions))
                                .pipe(
                                    take(1),
                                    switchMap((institutions: InstitutionsVendorListModel[]) => this.store.pipe(
                                        select(getAuthenticatedUserPreferences),
                                        map(preferences => preferences ? preferences.getPreferencesItemValue('currentApp') : ''),
                                        filter(currentAppId => !!currentAppId),
                                        take(1)
                                    ))
                                )
                                .subscribe((currentAppId: string) => {
                                    this.store.dispatch(new integrationActions.GetVendorInstitutionsAction({currentAppId: currentAppId}));
                                })
                        );
                    }
                } else {
                    if (isProvider || isVendor) {
                        this.store.dispatch(new integrationActions.GetPendingRequestsAction());
                    }

                    if (isProvider) {
                        this.store.dispatch(new integrationActions.GetProviderApplicationsAction());
                    } else if (isVendor) {
                        this.subscriptions.push(
                            this.store.pipe(
                                select(getIsDeletingApplication),
                                filter((isDeletingApplication: boolean) => !isDeletingApplication),
                                take(1),
                                switchMap(() => this.store.pipe(
                                    select(getAuthenticatedUserPreferences),
                                    map(preferences => preferences ? preferences.getPreferencesItemValue('currentApp') : ''),
                                    filter((currentAppId: string) => !!currentAppId),
                                    take(1)
                                ))
                            ).subscribe((currentAppId: string) => {
                                this.store.dispatch(new integrationActions.GetVendorInstitutionsAction({currentAppId: currentAppId}));
                            })
                        );
                    }
                }
            })
        );
    }

    private isMainModuleRoute(url: string) {
        return mainModuleRoutes.some((route: Route) => url.endsWith(`/${route.path}`));
    }

    ngOnInit() {
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
